import { useEffect, useRef, useState } from "react";
import {
    CheckIcon,
    ChevronDownIcon,
    CubeIcon,
    HashtagIcon,
    MagnifyingGlassIcon,
    MinusIcon,
    PlusIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";
import CopyBomPicker from "./CopyBomPicker";

function cn(...parts) {
    return parts.filter(Boolean).join(" ");
}

function formatComponentCost(value) {
    const amount = Number(value || 0);
    return `$${amount.toFixed(amount > 0 && amount < 0.1 ? 4 : 2)}`;
}

function ActionPopover({ open, onClose, children }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleMouseDown = (event) => {
            if (event.target?.closest?.("[data-select-portal-panel='true']")) return;
            if (ref.current && !ref.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            ref={ref}
            className="absolute left-0 top-full z-50 mt-1.5 min-w-[280px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        >
            {children}
        </div>
    );
}

const BomBulkBar = ({
    selectedCount,
    componentLibrary = [],
    componentOptions = [],
    boxPresets = [],
    vendorOptions = [],
    vendorSelectLoading = false,
    busy = false,
    onApplyAddComponent,
    onCreateComponentLibraryItem,
    onOpenComponentLibrary,
    onRemoveComponent,
    onUpdateComponentQty,
    onApplyBoxPreset,
    productRows = [],
    onCopyBom,
    onClear,
}) => {
    const [active, setActive] = useState(null);
    const [openSelectKey, setOpenSelectKey] = useState(null);

    const [addSearch, setAddSearch] = useState("");
    const [selectedLibraryId, setSelectedLibraryId] = useState("");
    const [draftQty, setDraftQty] = useState("1");
    const [draftUnitCost, setDraftUnitCost] = useState("0");
    const [draftVendorId, setDraftVendorId] = useState("");
    const [isCreatingLibraryComponent, setIsCreatingLibraryComponent] = useState(false);
    const [creatingLibraryComponent, setCreatingLibraryComponent] = useState(false);
    const [createdLibraryComponent, setCreatedLibraryComponent] = useState(null);
    const [newComponentName, setNewComponentName] = useState("");
    const [newComponentType, setNewComponentType] = useState("");
    const [newComponentUnitCost, setNewComponentUnitCost] = useState("");
    const [newComponentQty, setNewComponentQty] = useState("1");
    const [newComponentVendorId, setNewComponentVendorId] = useState("");

    const [removeSearch, setRemoveSearch] = useState("");
    const [selectedRemoveComponent, setSelectedRemoveComponent] = useState("");

    const [qtySearch, setQtySearch] = useState("");
    const [selectedQtyComponent, setSelectedQtyComponent] = useState("");
    const [newQty, setNewQty] = useState("");

    const [presetSearch, setPresetSearch] = useState("");
    const [selectedPresetId, setSelectedPresetId] = useState("");
    const [copySourceId, setCopySourceId] = useState("");
    const [copyMode, setCopyMode] = useState("replace");

    const resetPopoverState = () => {
        setOpenSelectKey(null);

        setAddSearch("");
        setSelectedLibraryId("");
        setDraftQty("1");
        setDraftUnitCost("0");
        setDraftVendorId("");
        setIsCreatingLibraryComponent(false);
        setCreatingLibraryComponent(false);
        setCreatedLibraryComponent(null);
        setNewComponentName("");
        setNewComponentType("");
        setNewComponentUnitCost("");
        setNewComponentQty("1");
        setNewComponentVendorId("");

        setRemoveSearch("");
        setSelectedRemoveComponent("");

        setQtySearch("");
        setSelectedQtyComponent("");
        setNewQty("");

        setPresetSearch("");
        setSelectedPresetId("");
        setCopySourceId("");
        setCopyMode("replace");
    };

    useEffect(() => {
        if (!active) {
            setOpenSelectKey(null);
        }
    }, [active]);

    const closePopovers = () => {
        setActive(null);
        resetPopoverState();
    };

    const toggle = (key) => {
        if (busy) return;
        setActive((prev) => (prev === key ? null : key));
        setOpenSelectKey(null);
    };

    const resolveVendorId = (component) => {
        const rawVendorId =
            component?.vendorId != null && component.vendorId !== ""
                ? String(component.vendorId)
                : component?.vendor_id != null && component.vendor_id !== ""
                  ? String(component.vendor_id)
                  : "";
        if (rawVendorId) return rawVendorId;

        const supplierLabel = String(component?.defaultSupplier || "").trim().toLowerCase();
        const matchedVendor = (vendorOptions || []).find(
            (option) => String(option.label || "").trim().toLowerCase() === supplierLabel
        );
        return matchedVendor ? String(matchedVendor.value) : "";
    };

    const filteredLibrary = (componentLibrary || []).filter((component) => {
        const query = addSearch.trim().toLowerCase();
        if (!query) return true;
        const haystack = [
            component?.name,
            component?.type,
            component?.notes,
            component?.description,
            component?.defaultSupplier,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return haystack.includes(query);
    });

    const selectedLibraryComponent =
        (componentLibrary || []).find((component) => String(component.id) === String(selectedLibraryId)) ||
        (createdLibraryComponent && String(createdLibraryComponent.id) === String(selectedLibraryId)
            ? createdLibraryComponent
            : null);

    const filteredExistingComponents = (componentOptions || []).filter((item) => {
        const query = (active === "remove" ? removeSearch : qtySearch).trim().toLowerCase();
        if (!query) return true;
        return String(item.label || item.value || "")
            .toLowerCase()
            .includes(query);
    });

    const selectedQtyMeta = (componentOptions || []).find(
        (item) => String(item.value) === String(selectedQtyComponent)
    );

    const filteredPresets = (boxPresets || []).filter((preset) => {
        const query = presetSearch.trim().toLowerCase();
        if (!query) return true;
        const haystack = [preset?.name, preset?.notes, ...(preset?.components || []).map((item) => item.component || item.name)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return haystack.includes(query);
    });

    const selectedPreset = (boxPresets || []).find((preset) => String(preset.id) === String(selectedPresetId));

    if (!selectedCount) return null;

    const selectLibraryComponent = (component) => {
        setSelectedLibraryId(String(component.id));
        setCreatedLibraryComponent(component);
        setIsCreatingLibraryComponent(false);
        setDraftQty(String(Number(component.defaultQty ?? 1) || 1));
        setDraftUnitCost(String(Number(component.defaultUnitCost ?? 0) || 0));
        setDraftVendorId(resolveVendorId(component));
    };

    const handleCreateAndSelectComponent = async () => {
        const name = String(newComponentName || "").trim();
        if (!name || creatingLibraryComponent || !onCreateComponentLibraryItem) return;

        setCreatingLibraryComponent(true);
        try {
            const created = await onCreateComponentLibraryItem({
                component: name,
                description: String(newComponentType || "").trim() || null,
                qty: Number(newComponentQty || 1) || 1,
                cost: Number(newComponentUnitCost || 0) || 0,
                notes: null,
                vendorId: newComponentVendorId ? String(newComponentVendorId).trim() : null,
            });
            if (!created?.id) return;
            selectLibraryComponent(created);
        } catch {
            // Parent surfaces create errors.
        } finally {
            setCreatingLibraryComponent(false);
        }
    };

    const actionButton = (key, label, Icon, onClick) => (
        <button
            type="button"
            onClick={onClick || (() => toggle(key))}
            disabled={busy}
            className={cn(
                "inline-flex h-8 items-center gap-1 rounded-md bg-primary px-3 text-xs font-medium transition-colors",
                active === key
                    ? "border-white bg-white text-gray-900"
                    : "bg-primary text-white hover:bg-white/10",
                busy ? "cursor-not-allowed opacity-50" : ""
            )}
        >
            {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
            <span>{label}</span>
            {onClick ? null : <ChevronDownIcon className="h-3 w-3 text-current/70" />}
        </button>
    );

    return (
        <div className="mx-4 mb-2 rounded-lg bg-[#111827] px-3 py-2 text-white">
            <div className="relative flex flex-wrap items-center gap-2">
                <div className="mr-1 flex items-center gap-2 border-r border-white/10 pr-3">
                    <span className="rounded-md bg-black/20 px-2.5 py-1 text-xs font-semibold tabular-nums">
                        {selectedCount} selected
                    </span>
                </div>

                <div className="relative">
                    {actionButton("add", "Add Component", PlusIcon)}
                    <ActionPopover open={active === "add"} onClose={closePopovers}>
                        <div className="p-3">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                Add Component to {selectedCount} BOM{selectedCount === 1 ? "" : "s"}
                            </p>

                            {isCreatingLibraryComponent ? (
                                <>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="mb-1 block text-[10px] text-gray-500">Name *</label>
                                            <input
                                                autoFocus
                                                value={newComponentName}
                                                onChange={(event) => setNewComponentName(event.target.value)}
                                                placeholder="e.g. Wallet Sticker"
                                                className="h-8 w-full rounded-md border border-gray-200 px-2 text-xs text-gray-900 outline-none focus:border-gray-400"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-[10px] text-gray-500">Type / Description</label>
                                            <input
                                                value={newComponentType}
                                                onChange={(event) => setNewComponentType(event.target.value)}
                                                placeholder="e.g. Round Kings Loot Sticker"
                                                className="h-8 w-full rounded-md border border-gray-200 px-2 text-xs text-gray-900 outline-none focus:border-gray-400"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="mb-1 block text-[10px] text-gray-500">Unit Cost ($)</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={0.001}
                                                    value={newComponentUnitCost}
                                                    onChange={(event) => setNewComponentUnitCost(event.target.value)}
                                                    className="h-8 w-full no-arrows-by-input rounded-md border border-gray-200 px-2 text-right text-xs text-gray-900 outline-none focus:border-gray-400"
                                                />
                                            </div>
                                            <div>
                                                <label className="mb-1 block text-[10px] text-gray-500">Default Qty</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    value={newComponentQty}
                                                    onChange={(event) => setNewComponentQty(event.target.value)}
                                                    className="h-8 w-full no-arrows-by-input rounded-md border border-gray-200 px-2 text-right text-xs text-gray-900 outline-none focus:border-gray-400"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-[10px] text-gray-500">Default Supplier</label>
                                            <AddDrawerSelect
                                                options={vendorOptions}
                                                value={newComponentVendorId}
                                                onChange={(value) =>
                                                    setNewComponentVendorId(
                                                        value === undefined || value === null ? "" : String(value)
                                                    )
                                                }
                                                noneLabel="Select supplier..."
                                                emptyLabel="Select supplier..."
                                                isLoading={vendorSelectLoading}
                                                disabled={vendorSelectLoading || creatingLibraryComponent}
                                                renderInPortal
                                                stackKey="bulk-new-component-supplier"
                                                openStackKey={openSelectKey}
                                                onStackKeyChange={setOpenSelectKey}
                                                selectClassname="w-full"
                                                buttonSelectedClassname="!py-1.5"
                                            />
                                        </div>
                                        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => setIsCreatingLibraryComponent(false)}
                                                disabled={creatingLibraryComponent}
                                                className="h-8 rounded-md border border-gray-200 px-3 text-xs text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleCreateAndSelectComponent}
                                                disabled={
                                                    creatingLibraryComponent ||
                                                    vendorSelectLoading ||
                                                    !String(newComponentName || "").trim()
                                                }
                                                className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-xs text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <CheckIcon className="h-3 w-3" />
                                                {creatingLibraryComponent ? "Saving..." : "Save & Select"}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : !selectedLibraryComponent ? (
                                <>
                                    <div className="relative mb-2">
                                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                        <input
                                            autoFocus
                                            value={addSearch}
                                            onChange={(event) => setAddSearch(event.target.value)}
                                            placeholder="Search component library..."
                                            className="h-8 w-full rounded-md border border-gray-200 pl-7 pr-2 text-xs text-gray-900 outline-none focus:border-gray-400"
                                        />
                                    </div>

                                    <div className="max-h-[190px] overflow-y-auto">
                                        {filteredLibrary.slice(0, 30).map((component) => (
                                            <button
                                                key={component.id}
                                                type="button"
                                                onClick={() => selectLibraryComponent(component)}
                                                className="flex w-full items-start justify-between gap-2 rounded-md px-2 py-2 text-left hover:bg-gray-50"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <span className="block truncate text-xs font-medium text-gray-900">
                                                        {component.name}
                                                    </span>
                                                    <span className="block truncate text-[10px] text-gray-400">
                                                        {component.type || component.notes || component.description || "—"}
                                                    </span>
                                                </div>
                                                <span className="shrink-0 text-[10px] tabular-nums text-gray-500">
                                                    {formatComponentCost(component.defaultUnitCost)}
                                                </span>
                                            </button>
                                        ))}
                                        {filteredLibrary.length === 0 ? (
                                            <p className="py-3 text-center text-xs text-gray-400">
                                                No components found
                                            </p>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2">
                                        <div className="min-w-0">
                                            <span className="block truncate text-xs font-semibold text-gray-900">
                                                {selectedLibraryComponent.name}
                                            </span>
                                            <span className="block truncate text-[10px] text-gray-400">
                                                {selectedLibraryComponent.type ||
                                                    selectedLibraryComponent.notes ||
                                                    "Component Library"}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedLibraryId("")}
                                            className="text-[10px] text-gray-400 underline hover:text-gray-700"
                                        >
                                            change
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="mb-1 block text-[10px] text-gray-500">Qty</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={draftQty}
                                                onChange={(event) => setDraftQty(event.target.value)}
                                                className="h-8 w-full no-arrows-by-input rounded-md border border-gray-200 px-2 text-right text-xs text-gray-900 outline-none focus:border-gray-400"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-[10px] text-gray-500">Unit Cost ($)</label>
                                            <input
                                                type="number"
                                                min={0}
                                                step={0.001}
                                                value={draftUnitCost}
                                                onChange={(event) => setDraftUnitCost(event.target.value)}
                                                className="h-8 w-full no-arrows-by-input rounded-md border border-gray-200 px-2 text-right text-xs text-gray-900 outline-none focus:border-gray-400"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-3">
                                        <label className="mb-1 block text-[10px] text-gray-500">Supplier</label>
                                        <AddDrawerSelect
                                            options={vendorOptions}
                                            value={draftVendorId}
                                            onChange={(value) =>
                                                setDraftVendorId(
                                                    value === undefined || value === null ? "" : String(value)
                                                )
                                            }
                                            noneLabel="Select supplier..."
                                            emptyLabel="Select supplier..."
                                            isLoading={vendorSelectLoading}
                                            disabled={vendorSelectLoading}
                                            renderInPortal
                                            stackKey="bulk-add-component-supplier"
                                            openStackKey={openSelectKey}
                                            onStackKeyChange={setOpenSelectKey}
                                            selectClassname="w-full"
                                            buttonSelectedClassname="!py-1.5"
                                        />
                                    </div>

                                    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
                                        <span className="text-[10px] text-gray-400">
                                            Ext:{" "}
                                            <strong className="font-semibold text-gray-600">
                                                {formatComponentCost(
                                                    (parseFloat(draftQty) || 0) * (parseFloat(draftUnitCost) || 0)
                                                )}
                                            </strong>{" "}
                                            per unit
                                        </span>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => {
                                                const qty = parseFloat(draftQty);
                                                const unitCost = parseFloat(draftUnitCost);
                                                const description = String(
                                                    selectedLibraryComponent.description || ""
                                                ).trim();
                                                const componentPresetId =
                                                    selectedLibraryComponent.componentPresetId != null &&
                                                    String(selectedLibraryComponent.componentPresetId).trim() !== ""
                                                        ? String(selectedLibraryComponent.componentPresetId).trim()
                                                        : selectedLibraryComponent.component_preset_id != null &&
                                                            String(selectedLibraryComponent.component_preset_id).trim() !==
                                                                ""
                                                          ? String(selectedLibraryComponent.component_preset_id).trim()
                                                          : selectedLibraryComponent.id != null &&
                                                              String(selectedLibraryComponent.id).trim() !== ""
                                                            ? String(selectedLibraryComponent.id).trim()
                                                            : "";
                                                onApplyAddComponent?.({
                                                    id: `bulk-add-${Date.now()}`,
                                                    componentName: String(selectedLibraryComponent.name || "").trim(),
                                                    componentType: String(selectedLibraryComponent.type || "").trim(),
                                                    description,
                                                    supplier:
                                                        (
                                                            vendorOptions.find(
                                                                (option) =>
                                                                    String(option.value) === String(draftVendorId)
                                                            ) || {}
                                                        ).label || "",
                                                    vendorId: draftVendorId,
                                                    componentPresetId,
                                                    component_preset_id: componentPresetId || null,
                                                    qty: Number.isFinite(qty) ? qty : 1,
                                                    unitCost: Number.isFinite(unitCost) ? unitCost : 0,
                                                    extendedCost:
                                                        (Number.isFinite(qty) ? qty : 1) *
                                                        (Number.isFinite(unitCost) ? unitCost : 0),
                                                    isNewComponent: false,
                                                });
                                                closePopovers();
                                            }}
                                            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-xs text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CheckIcon className="h-3 w-3" />
                                            Apply to {selectedCount}
                                        </button>
                                    </div>
                                </>
                            )}
                            {!isCreatingLibraryComponent && !selectedLibraryComponent ? (
                                <div className="border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedLibraryId("");
                                            setIsCreatingLibraryComponent(true);
                                            setNewComponentName(addSearch.trim());
                                            setNewComponentType("");
                                            setNewComponentUnitCost("");
                                            setNewComponentQty("1");
                                            setNewComponentVendorId("");
                                        }}
                                        className="mt-2 flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                                    >
                                        <PlusIcon className="h-4 w-4 text-gray-400" />
                                        <span>New Component...</span>
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </ActionPopover>
                </div>

                <div className="relative">
                    {actionButton("copy", "Copy BOM from product", CubeIcon)}
                    <ActionPopover open={active === "copy"} onClose={closePopovers}>
                        <div className="p-3">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                Copy BOM to {selectedCount} selected SKU{selectedCount === 1 ? "" : "s"}
                            </p>
                            <CopyBomPicker
                                products={productRows}
                                selectedProductId={copySourceId}
                                onSelect={(source) => setCopySourceId(String(source?.id || ""))}
                                label="Source product"
                            />
                            <div className="mt-3 space-y-1.5">
                                <label className="flex items-center gap-2 text-[11px] text-gray-700">
                                    <input
                                        type="radio"
                                        checked={copyMode === "replace"}
                                        onChange={() => setCopyMode("replace")}
                                    />
                                    Replace entire BOM
                                </label>
                                <label className="flex items-center gap-2 text-[11px] text-gray-700">
                                    <input
                                        type="radio"
                                        checked={copyMode === "merge"}
                                        onChange={() => setCopyMode("merge")}
                                    />
                                    Merge into existing BOM
                                </label>
                            </div>
                            <div className="mt-3 flex justify-end border-t border-gray-100 pt-2">
                                <button
                                    type="button"
                                    disabled={busy || !copySourceId}
                                    onClick={() => {
                                        onCopyBom?.({ sourceProductId: copySourceId, mode: copyMode });
                                        closePopovers();
                                    }}
                                    className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-xs text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <CheckIcon className="h-3 w-3" />
                                    Apply to {selectedCount}
                                </button>
                            </div>
                        </div>
                    </ActionPopover>
                </div>

                {actionButton("library", "Component Library", CubeIcon, onOpenComponentLibrary)}

                <div className="relative">
                    {actionButton("remove", "Remove Component", MinusIcon)}
                    <ActionPopover open={active === "remove"} onClose={closePopovers}>
                        <div className="p-3">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                Remove Component from {selectedCount} BOM{selectedCount === 1 ? "" : "s"}
                            </p>
                            <div className="relative mb-2">
                                <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                <input
                                    autoFocus
                                    value={removeSearch}
                                    onChange={(event) => setRemoveSearch(event.target.value)}
                                    placeholder="Search components in selected BOMs..."
                                    className="h-8 w-full rounded-md border border-gray-200 pl-7 pr-2 text-xs text-gray-900 outline-none focus:border-gray-400"
                                />
                            </div>
                            <div className="mb-3 max-h-[190px] overflow-y-auto">
                                {filteredExistingComponents.map((item) => {
                                    const isSelected = String(selectedRemoveComponent) === String(item.value);
                                    return (
                                        <button
                                            key={item.value}
                                            type="button"
                                            onClick={() => setSelectedRemoveComponent(String(item.value))}
                                            className={cn(
                                                "flex w-full items-center justify-between rounded-md px-2 py-2 text-left hover:bg-gray-50",
                                                isSelected ? "bg-red-50 text-red-600" : "text-gray-900"
                                            )}
                                        >
                                            <span className="truncate text-xs">{item.label}</span>
                                            {isSelected ? <CheckIcon className="h-3.5 w-3.5" /> : null}
                                        </button>
                                    );
                                })}
                                {filteredExistingComponents.length === 0 ? (
                                    <p className="py-3 text-center text-xs text-gray-400">
                                        No components found in selected BOMs
                                    </p>
                                ) : null}
                            </div>
                            {selectedRemoveComponent ? (
                                <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                                    <span className="pr-3 text-[10px] text-red-500">
                                        Will remove &quot;{selectedRemoveComponent}&quot; from all selected BOMs
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={closePopovers}
                                            className="h-8 rounded-md border border-gray-200 px-3 text-xs text-gray-600 hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => {
                                                onRemoveComponent?.(selectedRemoveComponent);
                                                closePopovers();
                                            }}
                                            className="h-8 rounded-md bg-red-600 px-3 text-xs text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </ActionPopover>
                </div>

                <div className="relative">
                    {actionButton("qty", "Edit QTY", HashtagIcon)}
                    <ActionPopover open={active === "qty"} onClose={closePopovers}>
                        <div className="p-3">
                            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                Edit Component Qty in {selectedCount} BOM{selectedCount === 1 ? "" : "s"}
                            </p>

                            {!selectedQtyComponent ? (
                                <>
                                    <div className="relative mb-2">
                                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                        <input
                                            autoFocus
                                            value={qtySearch}
                                            onChange={(event) => setQtySearch(event.target.value)}
                                            placeholder="Search components..."
                                            className="h-8 w-full rounded-md border border-gray-200 pl-7 pr-2 text-xs text-gray-900 outline-none focus:border-gray-400"
                                        />
                                    </div>
                                    <div className="max-h-[190px] overflow-y-auto">
                                        {filteredExistingComponents.map((item) => (
                                            <button
                                                key={item.value}
                                                type="button"
                                                onClick={() => {
                                                    setSelectedQtyComponent(String(item.value));
                                                    setNewQty("");
                                                }}
                                                className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left hover:bg-gray-50"
                                            >
                                                <span className="truncate text-xs text-gray-900">{item.label}</span>
                                                <span className="shrink-0 text-[10px] text-gray-400">
                                                    {item.qtyLabel || ""}
                                                </span>
                                            </button>
                                        ))}
                                        {filteredExistingComponents.length === 0 ? (
                                            <p className="py-3 text-center text-xs text-gray-400">
                                                No components found
                                            </p>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2">
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-900">
                                                {selectedQtyComponent}
                                            </span>
                                            {selectedQtyMeta?.qtyLabel ? (
                                                <span className="block text-[10px] text-gray-400">
                                                    {selectedQtyMeta.qtyLabel}
                                                </span>
                                            ) : null}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedQtyComponent("")}
                                            className="text-[10px] text-gray-400 underline hover:text-gray-700"
                                        >
                                            change
                                        </button>
                                    </div>
                                    <label className="mb-1 block text-[10px] text-gray-500">New Quantity</label>
                                    <input
                                        autoFocus
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={newQty}
                                        onChange={(event) => setNewQty(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" && newQty !== "") {
                                                onUpdateComponentQty?.(
                                                    selectedQtyComponent,
                                                    parseFloat(newQty) || 0
                                                );
                                                closePopovers();
                                            }
                                        }}
                                        className="h-8 w-full no-arrows-by-input rounded-md border border-gray-200 px-2 text-right text-xs text-gray-900 outline-none focus:border-gray-400"
                                    />
                                    <div className="mt-3 flex justify-end border-t border-gray-100 pt-2">
                                        <button
                                            type="button"
                                            disabled={busy || newQty === ""}
                                            onClick={() => {
                                                onUpdateComponentQty?.(
                                                    selectedQtyComponent,
                                                    parseFloat(newQty) || 0
                                                );
                                                closePopovers();
                                            }}
                                            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-xs text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CheckIcon className="h-3 w-3" />
                                            Update {selectedCount} BOM{selectedCount === 1 ? "" : "s"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </ActionPopover>
                </div>

                <div className="relative">
                    {actionButton("preset", "Apply Box Preset", CubeIcon)}
                    <ActionPopover open={active === "preset"} onClose={closePopovers}>
                        <div className="p-3">
                            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                Apply Box Preset to {selectedCount} BOM{selectedCount === 1 ? "" : "s"}
                            </p>
                            <p className="mb-2 text-[10px] text-gray-400">
                                Replaces existing Box/Insert/Sleeve components with the selected preset.
                            </p>

                            {!selectedPreset ? (
                                <>
                                    <div className="relative mb-2">
                                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                        <input
                                            autoFocus
                                            value={presetSearch}
                                            onChange={(event) => setPresetSearch(event.target.value)}
                                            placeholder="Search presets..."
                                            className="h-8 w-full rounded-md border border-gray-200 pl-7 pr-2 text-xs text-gray-900 outline-none focus:border-gray-400"
                                        />
                                    </div>
                                    <div className="max-h-[190px] overflow-y-auto">
                                        {filteredPresets.map((preset) => (
                                            <button
                                                key={preset.id}
                                                type="button"
                                                onClick={() => setSelectedPresetId(String(preset.id))}
                                                className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left hover:bg-gray-50"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <span className="block truncate text-xs font-medium text-gray-900">
                                                        {preset.name}
                                                    </span>
                                                    <span className="block truncate text-[10px] text-gray-400">
                                                        {(preset.components || []).length} items
                                                    </span>
                                                </div>
                                                <span className="shrink-0 text-[10px] font-semibold tabular-nums text-gray-700">
                                                    {formatComponentCost(preset.totalCost)}
                                                </span>
                                            </button>
                                        ))}
                                        {filteredPresets.length === 0 ? (
                                            <p className="py-3 text-center text-xs text-gray-400">
                                                No presets found
                                            </p>
                                        ) : null}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-2 flex items-center justify-between border-b border-gray-100 pb-2">
                                        <div>
                                            <span className="block text-xs font-semibold text-gray-900">
                                                {selectedPreset.name}
                                            </span>
                                            <span className="block text-[10px] text-gray-400">
                                                {formatComponentCost(selectedPreset.totalCost)}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelectedPresetId("")}
                                            className="text-[10px] text-gray-400 underline hover:text-gray-700"
                                        >
                                            change
                                        </button>
                                    </div>
                                    <div className="mb-3 space-y-1">
                                        {(selectedPreset.components || []).map((component) => (
                                            <div
                                                key={component.id || component.component || component.name}
                                                className="flex items-center justify-between text-[10px]"
                                            >
                                                <span className="truncate text-gray-700">
                                                    {component.component || component.name}
                                                </span>
                                                <span className="shrink-0 tabular-nums text-gray-400">
                                                    {formatComponentCost(component.unitCost)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex justify-end border-t border-gray-100 pt-2">
                                        <button
                                            type="button"
                                            disabled={busy}
                                            onClick={() => {
                                                onApplyBoxPreset?.({
                                                    ...selectedPreset,
                                                    components: (selectedPreset.components || []).map((component) => ({
                                                        ...component,
                                                        componentPresetId: "",
                                                        component_preset_id: null,
                                                    })),
                                                });
                                                closePopovers();
                                            }}
                                            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-xs text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CheckIcon className="h-3 w-3" />
                                            Apply to {selectedCount}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </ActionPopover>
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onClear}
                        disabled={busy}
                        className="inline-flex items-center gap-1 text-xs text-white/60 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <XMarkIcon className="h-3.5 w-3.5" />
                        Clear
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BomBulkBar;
